import styled from "styled-components";
import { Card } from "antd";

// Define breakpoint inline
const device = {
  mobileS: '(max-width: 767px)'
};

export const TranslationContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 100px);
  width: calc(100% + 170px);
  padding: 0;
  margin: 0 -85px;
  background-color: ${({ theme }) => theme.colors.colorBgBase};
  
  @media ${device.mobileS} {
    flex-direction: column;
    width: 100%;
    margin: 0;
  }
`;

export const SidebarStyled = styled.aside`
  width: 280px;
  min-width: 200px;
  background-color: ${({ theme }) => theme.colors.colorBgContainer};
  border-right: 1px solid #BFBFBF;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex-shrink: 0;
  max-height: 100vh;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.colorFillTertiary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.colorTextSecondary};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.colorText};
    }
  }

  @media ${device.mobileS} {
    width: 100%;
    min-width: 100%;
    border-right: none;
    border-bottom: 1px solid ${({ theme }) => theme.colors.colorBorder};
    max-height: none;
  }
`;


export const SidebarTitle = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 4px 0;
  color: ${({ theme }) => theme.colors.colorText};
`;

export const SidebarSubtitle = styled.p`
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
`;

export const SidebarSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SidebarSectionScrollable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: calc(100vh - 300px);
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    width: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.colorFillTertiary};
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.colorTextSecondary};
    border-radius: 3px;
    
    &:hover {
      background: ${({ theme }) => theme.colors.colorText};
    }
  }
`;

export const SidebarLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.colorTextSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ScreenButton = styled.button<{ $active?: boolean }>`
  width: 100%;
  text-align: left;
  padding: 6px 10px;
  border-radius: 6px;
  font-size: 15px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.colorPrimaryBg : "transparent"};
  color: ${({ $active, theme }) =>
    $active ? theme.colors.colorPrimary : theme.colors.colorText};
  font-weight: ${({ $active }) => ($active ? 500 : 400)};

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active ? theme.colors.colorPrimaryBg : theme.colors.colorFillTertiary};
  }
`;

export const MainContent = styled.main`
  flex: 1;
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-x: auto;
  min-width: 0;

  @media ${device.mobileS} {
    padding: 8px;
  }
`;


export const MetricsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;

  @media ${device.mobileS} {
    grid-template-columns: 1fr;
  }
`;

export const MetricCard = styled(Card)<{ $warning?: boolean; $success?: boolean }>`
  border-radius: 16px;
  .ant-card-body {
    padding: 16px;
    background-color: ${({ $warning, $success }) => {
      if ($success) return "rgba(34, 197, 94, 0.1)";
      if ($warning) return "rgba(220, 38, 38, 0.1)";
      return "transparent";
    }};
  }
`;

export const MetricLabel = styled.p`
  font-size: 18px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.colorTextSecondary};
  margin: 0 0 8px 0;
`;

export const MetricValue = styled.p`
  font-size: 28px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.colorText};
  margin: 0;
`;

export const SearchContainer = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  gap: 12px;
  align-items: between-center;
  justify-content: space-between;
  flex-wrap: wrap;
`;

export const SearchIcon = styled.span`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 16px;
  color: ${({ theme }) => theme.colors.colorTextSecondary};
`;

export const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

export const TabButton = styled.button<{ $active?: boolean }>`
  padding: 8px 16px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 500 : 500)};
  border: 1px solid ${({ theme }) => theme.colors.colorBorder};
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ $active, theme }) =>
    $active ? theme.colors.primary100 : theme.colors.colorBgContainer};
  color: ${({ theme }) => theme.colors.colorText};

  &:hover {
    color: ${({ theme }) => theme.colors.colorText};
    background-color: ${({ $active, theme }) =>
      $active ? theme.colors.primary200 : theme.colors.colorFillTertiary};
    border-color: ${({ theme }) => theme.colors.colorBorder};
  }
`;

export const TableCard = styled(Card)`
  border-radius: 16px;
  .ant-card-body {
    padding: 16px;
  }
  
  .ant-table {
    font-size: 15px;
  }
  
  .ant-table-thead > tr > th {
    font-size: 15px;
    font-weight: 600;
  }
  
  .ant-table-tbody > tr > td {
    font-size: 15px;
  }
`;

export const StatusBadge = styled.span<{ $status: "complete" | "partial" | "missing" }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 500;
  background-color: ${({ $status, theme }) => {
    switch ($status) {
      case "complete":
        return "#f0fdf4";
      case "partial":
        return "#fefce8";
      case "missing":
        return "#fef2f2";
      default:
        return theme.colors.colorFillTertiary;
    }
  }};
  color: ${({ $status }) => {
    switch ($status) {
      case "complete":
        return "#15803d";
      case "partial":
        return "#a16207";
      case "missing":
        return "#dc2626";
      default:
        return "#000";
    }
  }};
`;

export const KeyCell = styled.span`
  font-family: monospace;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.colorPrimary};
`;

export const EmptyText = styled.span`
  color: ${({ theme }) => theme.colors.colorTextSecondary};
`;